export enum DiagnosisCategory {
  IBD = 'IBD',
  IBS = 'IBS',
  Other = 'Other',
  UpperGI = 'Upper GI',
}

export type Diagnosis = {
  id: string;
  diagnosis: string;
  category?: DiagnosisCategory;
};

export interface UserDiagnosis extends Diagnosis {
  dx_id: string;
  sort_order: number | null;
}

export enum PrescriptionMedicationStatus {
  Active = 'ACTIVE',
  Error = 'ERROR',
  Future = 'FUTURE',
  Hidden = 'HIDDEN',
  Inactive = 'INACTIVE',
  Pending = 'PENDING',
  Reordered = 'REORDERED',
}

export type Medication = {
  resource_type: PrescriptionMedicationResourceType.Medication;
  id: string;
  name: string | null;
  dosage: string | null;
  directions: string | null;
  start_date: string | null;
  end_date: string | null;
  comment: string | null;
  status: PrescriptionMedicationStatus | null;
};

export type MedicationOption = {
  id: string;
  name: string;
};

export enum PrescriptionMedicationFilterType {
  Prescription = 'PRESCRIPTION',
  Medication = 'MEDICATION',
}

export enum PrescriptionMedicationResourceType {
  Prescription = 'Prescription',
  Medication = 'MedicationType',
}

export type Pharmacy = {
  id: string;
  name: string;
  state: string;
  city: string;
  zip: string;
  line1: string | null;
  line2: string | null;
};

export type Prescriber = {
  id: string;
  healthie_id: number;
  display_name: string | null;
  care_member_category: ProviderType | null;
};

export type Prescription = {
  resource_type: PrescriptionMedicationResourceType.Prescription;
  id: string;
  comment: string | null;
  dosage: string | null;
  directions: string | null;
  status: PrescriptionMedicationStatus | null;
  display_name: string;
  product_name: string;
  date_written: string | null;
  date_inactive: string | null;
  effective_date: string | null;
  pharmacy: Pharmacy | null;
  prescriber: Prescriber | null;
  refills: string | null;
  last_fill_date: string | null;
};

export enum ChartNoteStatus {
  TranscriptStarted = 'transcript_started',
  TranscriptAvailable = 'transcript_available',
  TranscriptFailed = 'transcript_failed',
  ChartNoteExists = 'chart_note_exists',
}

export type ChartNote = {
  is_locked: boolean | null;
  is_signed: boolean | null;
  note_id: string | null;
  note_label?: string | null;
  note_type: string;
  note_url: string | null;
  status: ChartNoteStatus | null;
};

export enum ProviderType {
  Coach = 'Coach',
  Pharmacist = 'Pharmacist',
  Provider = 'Provider',
  Dietitian = 'Dietitian',
}

// The status of the meeting
export enum PmStatus {
  occurred = 'Occurred',
  cancelled = 'Cancelled',
  lateCancelled = 'Late Cancellation',
  noShow = 'No-Show',
  rescheduled = 'Re-Scheduled',
}

export enum SupervisorStatus {
  supervised = 'supervised',
  supervising = 'supervising',
}

export type Provider = {
  id: number;
  display_name: string;
  full_name: string;
  healthie_id: number;
  image_url: string;
  status: string;
  supervisor_status: SupervisorStatus | null;
  type: ProviderType;
};

export enum LabResultStatus {
  Pending = 'Pending',
  Uploaded = 'Uploaded',
  InRange = 'In Range',
  OutOfRange = 'Out of Range',
}

// Matches the backend LabBillingType sent to POST .../lab-order.
export enum LabBillingType {
  Insurance = 'insurance',
  SelfPay = 'self_pay',
}

// Lab vendor scopes the lab-test search and is sent as the lab company on the
// create payload. Values match Healthie's spelling.
export enum LabVendor {
  Quest = 'Quest',
  Labcorp = 'Labcorp',
}

// A Healthie orderable lab option (one test), from GET .../lab-options.
export type LabOption = {
  id: string;
  name: string;
  lab_name: string | null;
};

export type LabOrderer = {
  id: number;
  name: string;
};

export type LabOrder = {
  // Our own Lab row id, not a Healthie id - pass as `lab_id` to
  // POST /user/{userId}/lab-result to attach a result to this order.
  id: string;
  lab: string | null;
  lab_company: string | null;
  created_at: string | null;
  result_status: LabResultStatus | null;
  orderer: LabOrderer | null;
  // Healthie document ids - fetch a document's content via
  // GET /user/{userId}/document/{id} only when actually needed.
  requisition_document_id: number | null;
  lab_result_document_id: number | null;
};

export type MeetingNote = {
  charting_note_url: string | null;
  chart_notes?: ChartNote[];
  client_confirmed: boolean;
  healthie_appointment_id: number;
  meeting_date: string;
  meeting_type: string | null;
  metadata: any;
  pm_status: PmStatus;
  provider?: Provider;
  zoom_join_url: string | null;
  zoom_start_url: string | null;
};
